import DebouncedInput from "@/components/DebouncedInput";
import { useMerchant } from "@/hooks/useMerchant";
import useNumberFormatter from "@/hooks/useNumberFormatter";
import { Box, Card, Text, VStack, chakra } from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { formatMonth } from "../../contracts/columns";
import { Contract } from "../../contracts/types";
import { useProtectTRPC } from "../../trpc";

type Props = {
  addedContractIds: string[];
  onAdd: (contract: Contract) => void;
};

const unavailableReason = (
  contract: Contract,
  addedContractIds: string[]
): string | undefined => {
  if (addedContractIds.includes(contract.id)) return "Ya está en la lista";
  if (contract.hasOpenClaim) return "Ya tiene una reclamación abierta";
  const retirement = contract.pendingChanges?.retirement;
  if (retirement)
    return `Tiene un retiro programado: sale de la fianza en ${formatMonth(
      retirement.effectiveMonth
    )}. Para reclamar, primero cancela el retiro en Contratos.`;
  return undefined;
};

const AddContractSearch = ({ addedContractIds, onAdd }: Props): JSX.Element => {
  const trpc = useProtectTRPC();
  const { merchant } = useMerchant();
  const formatCurrency = useNumberFormatter("es-CO", "COP", "currency");
  const [tenantDocumentNumber, setTenantDocumentNumber] = useState("");

  const documentNumber = tenantDocumentNumber.trim();
  const { data } = useQuery({
    ...trpc.contracts.search.queryOptions({
      rentalsMerchantId: merchant?.id ?? "",
      tenantDocumentNumber: documentNumber,
    }),
    enabled: !!merchant?.id && documentNumber.length > 0,
  });
  const contracts = data?.contracts ?? [];

  const handleAdd = (contract: Contract) => {
    onAdd(contract);
    setTenantDocumentNumber("");
  };

  return (
    <Card.Root
      borderRadius="lg"
      border="none"
      flexShrink={0}
      overflow="visible"
      zIndex={1}
      boxShadow="rgba(0, 0, 0, 0.05) 0px 1px 2px 0px"
    >
      <Card.Body p="4" gap={3}>
        <Text fontWeight={600}>Agregar otro contrato</Text>
        <Box position="relative">
          <DebouncedInput
            placeholder="Documento del arrendatario"
            value={tenantDocumentNumber}
            onDebouncedChange={setTenantDocumentNumber}
          />
          {documentNumber.length > 0 && data && (
            <VStack
              position="absolute"
              top="100%"
              left={0}
              right={0}
              mt={1}
              zIndex="dropdown"
              gap={0}
              align="stretch"
              bg="white"
              borderWidth="1px"
              borderColor="gray.200"
              borderRadius="md"
              boxShadow="md"
              maxH="320px"
              overflowY="auto"
            >
              {contracts.length === 0 && (
                <Text p={3} fontSize="sm" color="gray.600">
                  Sin resultados
                </Text>
              )}
              {contracts.map((contract) => {
                const reason = unavailableReason(contract, addedContractIds);

                return (
                  <chakra.button
                    key={contract.id}
                    type="button"
                    w="full"
                    textAlign="left"
                    borderBottomWidth="1px"
                    borderColor="gray.100"
                    _last={{ borderBottomWidth: 0 }}
                    bg="white"
                    p={3}
                    cursor="pointer"
                    _hover={{ bg: "palomma.50" }}
                    disabled={!!reason}
                    _disabled={{ cursor: "not-allowed", opacity: 0.6 }}
                    onClick={() => handleAdd(contract)}
                  >
                    <Text fontWeight={600}>
                      Contrato {contract.contractNumber} · {contract.tenantName}
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      {contract.propertyAddress || "Sin dirección"} · Canon
                      mensual: {formatCurrency(contract.monthlyRent)}
                    </Text>
                    {reason && (
                      <Text fontSize="sm" color="#EC5858" mt={1}>
                        {reason}
                      </Text>
                    )}
                  </chakra.button>
                );
              })}
            </VStack>
          )}
        </Box>
      </Card.Body>
    </Card.Root>
  );
};

export default AddContractSearch;
